import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import * as React from "react";

/** ===== NodeView (só aparece dentro do editor) ===== */
const FileAttachmentComponent = (props: any) => {
  const { node } = props;
  const { href, name, mime, size } = node.attrs;
  const sizeKB = size ? Math.round(Number(size) / 1024) : null;
  const isPdf = String(mime).toLowerCase() === "application/pdf";
  const isExcel = String(mime).toLowerCase() === "application/planilha";
  const isFileCompact = String(mime).toLowerCase() === "application/compactado";

  const renderIcon = () => {
    if (isPdf) {
      return "fa-file-pdf";
    } else if (isExcel) {
      return "fa-file-excel";
    } else if (isFileCompact) {
      return "fa-file-archive";
    }

    return "fa-paperclip";
  };

  return (
    <NodeViewWrapper className="tt-file-card">
      <i
        className={`fa-regular ${renderIcon()} tt-file-icon`}
        aria-hidden
      />
      <a
        href={href}
        download={name || true}
        target="_blank"
        rel="noopener noreferrer"
        className="tt-file-link"
      >
        {name || "arquivo"}
      </a>
      <div className="tt-file-meta">
        {mime || "application/octet-stream"}
        {sizeKB ? ` • ${sizeKB} KB` : ""}
      </div>
    </NodeViewWrapper>
  );
};

/** ===== Extensão ===== */
export const FileAttachment = Node.create({
  name: "fileAttachment",
  group: "block",
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      href: {
        default: null,
        // Suporta HTML salvo antigo (atributos no div) e novo (data-*)
        parseHTML: (el: HTMLElement) =>
          el.getAttribute("data-href") ||
          el.getAttribute("href") ||
          el.querySelector("a")?.getAttribute("href"),
        renderHTML: (attrs: any) => ({ "data-href": attrs.href }),
      },
      name: {
        default: null,
        parseHTML: (el: HTMLElement) =>
          el.getAttribute("data-name") ||
          el.getAttribute("name") ||
          el.querySelector("a")?.getAttribute("download") ||
          el.querySelector("a")?.textContent ||
          null,
        renderHTML: (attrs: any) => ({ "data-name": attrs.name }),
      },
      mime: {
        default: "application/octet-stream",
        parseHTML: (el: HTMLElement) =>
          el.getAttribute("data-mime") ||
          el.getAttribute("mime") ||
          el
            .querySelector(".tt-file-meta")
            ?.textContent?.split("•")?.[0]
            ?.trim() ||
          "application/octet-stream",
        renderHTML: (attrs: any) => ({ "data-mime": attrs.mime }),
      },
      size: {
        default: null,
        parseHTML: (el: HTMLElement) => {
          const raw =
            el.getAttribute("data-size") || el.getAttribute("size") || null;
          return raw ? Number(raw) : null;
        },
        renderHTML: (attrs: any) =>
          attrs.size != null ? { "data-size": String(attrs.size) } : {},
      },
    };
  },

  // Também aceita HTML salvo como <div data-type="file-attachment" ...>
  // (mantivemos igual ao seu)
  parseHTML() {
    return [{ tag: "div[data-type='file-attachment']" }];
  },

  /** Fallback HTML para quando NÃO há editor (SSR/viewer):
   *  renderiza um "card" auto-contido com link e meta.
   */
  renderHTML({ HTMLAttributes }) {
    const href = HTMLAttributes["data-href"] || HTMLAttributes.href || "#";
    const name =
      HTMLAttributes["data-name"] || HTMLAttributes.name || "arquivo";
    const mime =
      HTMLAttributes["data-mime"] ||
      HTMLAttributes.mime ||
      "application/octet-stream";
    const size = HTMLAttributes["data-size"] || HTMLAttributes.size || null;
    const sizeKB =
      size != null && !Number.isNaN(Number(size))
        ? `${Math.round(Number(size) / 1024)} KB`
        : "";

    const isPdf = String(mime).toLowerCase() === "application/pdf";
    const isExcel = String(mime).toLowerCase() === "application/planilha";
    const isFileCompact = String(mime).toLowerCase() === "application/compactado";

    const renderIcon = () => {
      if (isPdf) {
        return "fa-file-pdf";
      } else if (isExcel) {
        return "fa-file-excel";
      } else if (isFileCompact) {
        return "fa-file-archive";
      }

      return "fa-paperclip";
    };

    const iconClass = renderIcon();

    // Produz HTML estático completo (card + link + meta)
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "file-attachment",
        class: (HTMLAttributes.class || "") + " tt-file-card",
      }),
      [
        "i",
        {
          class: `fa-regular ${iconClass} tt-file-icon`,
          "aria-hidden": "true",
        },
      ],
      [
        "a",
        {
          href,
          download: name || true,
          target: "_blank",
          rel: "noopener noreferrer",
          class: "tt-file-link",
        },
        name,
      ],
      [
        "div",
        { class: "tt-file-meta" },
        `${mime}${sizeKB ? ` • ${sizeKB}` : ""}`,
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FileAttachmentComponent);
  },

  addCommands() {
    return {
      insertFileAttachment:
        (attrs: {
          href: string;
          name?: string;
          mime?: string;
          size?: number;
        }) =>
          ({ chain }) =>
            chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});
